import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface HotelImagePreviewProps {
  images: string[];
  initialIndex: number;
  open: boolean;
  alt?: string;
  onClose: () => void;
}

const previewIconButtonSx = {
  width: 40,
  height: 40,
  bgcolor: 'rgba(255, 255, 255, 0.86)',
  color: 'text.primary',
  transform: 'none',
  transition: 'background-color 150ms ease, box-shadow 150ms ease',
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
    transform: 'none',
  },
  '&:focus-visible': {
    outline: '2px solid rgba(255, 255, 255, 0.9)',
    outlineOffset: 2,
  },
};

export function HotelImagePreview({
  images,
  initialIndex,
  open,
  alt = '',
  onClose,
}: HotelImagePreviewProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, open]);

  const clampedIndex = Math.max(0, Math.min(currentIndex, images.length - 1));
  const showNav = images.length > 1;

  const handlePrev = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => (prev <= 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => (prev >= images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    },
    [handlePrev, handleNext],
  );

  useEffect(() => {
    if (!open || !showNav) return undefined;

    const handleWindowKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      e.preventDefault();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [handlePrev, handleNext, open, showNav]);

  const handleImageError = useCallback((src: string) => {
    setBrokenImages((prev) => new Set(prev).add(src));
  }, []);

  const currentSrc = images[clampedIndex];
  const isBroken = brokenImages.has(currentSrc);

  return (
    <Dialog
      PaperProps={{
        'aria-label': alt
          ? `${alt} — ${t('hotels.detail.imagePreview')}`
          : t('hotels.detail.imagePreview'),
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          maxWidth: '95vw',
          maxHeight: '95vh',
          m: 0,
          overflow: 'hidden',
        },
      }}
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open={open}
    >
      <div
        aria-label={t('hotels.detail.imagePreview')}
        className='relative flex min-h-[80vh] flex-col items-center justify-center outline-none'
        onKeyDown={handleKeyDown}
        role='dialog'
        tabIndex={-1}
      >
        {/* Close */}
        <div className='absolute right-2 top-2 z-10'>
          <IconButton
            aria-label={t('hotels.detail.closePreview')}
            disableRipple
            onClick={onClose}
            size='small'
            sx={previewIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Image */}
        <div className='flex items-center justify-center min-h-[40vh]'>
          {isBroken ? (
            <div className='flex flex-col items-center justify-center px-8 py-12 text-gray-400'>
              <svg
                aria-hidden='true'
                className='h-16 w-16'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                viewBox='0 0 24 24'
              >
                <path
                  d='M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <p className='mt-3 text-sm'>{t('hotels.detail.imageUnavailable')}</p>
            </div>
          ) : (
            <img
              alt={alt || t('hotels.detail.imagePreview')}
              className='max-h-[85vh] max-w-full object-contain rounded'
              onError={() => handleImageError(currentSrc)}
              src={currentSrc}
            />
          )}
        </div>

        {/* Nav arrows */}
        {showNav && (
          <>
            <div className='absolute left-2 top-1/2 -translate-y-1/2'>
              <IconButton
                aria-label={t('hotels.detail.previousImage')}
                disableRipple
                onClick={handlePrev}
                size='medium'
                sx={previewIconButtonSx}
              >
                <ChevronLeftIcon />
              </IconButton>
            </div>
            <div className='absolute right-2 top-1/2 -translate-y-1/2'>
              <IconButton
                aria-label={t('hotels.detail.nextImage')}
                disableRipple
                onClick={handleNext}
                size='medium'
                sx={previewIconButtonSx}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
          </>
        )}

        {/* Counter */}
        {showNav && (
          <div className='absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white'>
            {clampedIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </Dialog>
  );
}
